<script setup lang="ts">
import type { CompetitionSnapshot } from './competition-types';
import { challengeCountdown } from './competition.mjs';
import { challengeBannerEntry } from './challenge-banner.mjs';
import { useDailyChallenge } from './useDailyChallenge';
import { useBadgeCatalog } from '../creator/useBadgeCatalog';
import { trackEvent } from '~/utils/analytics';
import ChallengeBadge from './ChallengeBadge.vue';
import MmIcon from '../base/MmIcon.vue';

const props = defineProps<{ chart?: CompetitionSnapshot & { load_error?: boolean } }>();
const { t, locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const { data: mine, loading, isAuthReady, isAuthenticated } = useDailyChallenge();
const available = computed(() => !!props.chart?.challenge && !props.chart.load_error && (!isAuthReady.value || !isAuthenticated.value || loading.value || !!mine.value));
const { definitions, apiBase } = useBadgeCatalog(() => !!props.chart?.challenge);
const badge = computed(() => definitions.value.find(item => item.key === 'chart_topper'));
const entry = computed(() => challengeBannerEntry(mine.value));
const state = computed(() => entry.value.state);
// Serialize a shared clock so SSR and hydration render the same countdown.
const now = useState('challenge-banner-clock', () => Date.now());
const remaining = computed(() => challengeCountdown(props.chart?.challenge?.next_settlement_at, now.value));
const number = (value: number) => new Intl.NumberFormat(locale.value).format(value);
const status = computed(() => {
  const fragments: string[] = [];
  if (state.value === 'none') {
    const points = props.chart?.challenge?.rewards?.top1?.points;
    if (typeof points === 'number' && Number.isFinite(points)) fragments.push(t('mm.challenge.bannerPrize', { points: number(points) }));
  } else {
    fragments.push(t(state.value === 'qualified' ? 'mm.challenge.bannerQualified' : 'mm.challenge.bannerEntered', { title: entry.value.title }));
    if (state.value === 'entered' && entry.value.remaining !== undefined) fragments.push(t('mm.challenge.bannerRemaining', { count: number(entry.value.remaining) }));
  }
  if (state.value !== 'entered' && remaining.value) fragments.push(t('mm.challenge.countdown', remaining.value));
  return fragments.join(' · ');
});
const root = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | undefined;
let timer: ReturnType<typeof setInterval> | undefined;
let visible = false, reported = false;
const report = () => {
  if (reported || !visible || document.hidden || !isAuthReady.value || loading.value) return;
  reported = true;
  trackEvent('challenge_banner_show', { state: state.value, page_path: route.path });
};
const connect = () => {
  observer?.disconnect(); visible = false;
  if (!root.value || reported) return;
  observer = new IntersectionObserver(entries => {
    visible = entries[0]?.isIntersecting && entries[0]?.intersectionRatio >= .5;
    report();
  }, { threshold: [.5] });
  observer.observe(root.value);
};
watch(root, connect, { flush: 'post' });
watch([state, loading, isAuthReady, isAuthenticated], report);
onMounted(() => {
  now.value = Date.now();
  timer = setInterval(() => { now.value = Date.now(); }, 60000);
  connect(); document.addEventListener('visibilitychange', report);
});
onActivated(connect);
onDeactivated(() => { observer?.disconnect(); visible = false; });
onBeforeUnmount(() => { observer?.disconnect(); clearInterval(timer); document.removeEventListener('visibilitychange', report); });
const click = () => trackEvent('challenge_banner_click', { state: state.value, page_path: route.path });
</script>

<template>
  <div v-if="available" ref="root" class="challenge-banner-wrap">
    <NuxtLink :to="localePath('/music/ranking-music_daily_competition')" class="daily-challenge-banner" data-challenge-card :data-challenge-state="state" @click="click">
      <ChallengeBadge icon="chart_topper" :src="badge?.icon_url" :api-base="apiBase" :label="badge?.name || t('creator.badgesTitle')" />
      <span class="challenge-banner-copy"><strong>{{ t('mm.challenge.title') }}</strong><span v-if="status" class="challenge-banner-status" :title="status">{{ status }}</span></span>
      <MmIcon name="arrowR" :size="20" />
    </NuxtLink>
  </div>
</template>

<style scoped>
.daily-challenge-banner { display: flex; align-items: center; gap: var(--s-3); min-height: 76px; padding: var(--s-3) var(--s-4); border: 1px solid var(--line); border-radius: var(--r-lg); background: var(--surface); color: var(--text); }
.daily-challenge-banner:hover { background: var(--surface-2); }
.daily-challenge-banner:focus-visible { outline: 2px solid var(--focus); outline-offset: 3px; }
.daily-challenge-banner :deep(.challenge-badge) { width: 48px; height: 48px; }
.challenge-banner-copy { flex: 1; min-width: 0; display: grid; gap: var(--s-1); }
.challenge-banner-copy strong { font: 600 var(--t-body)/1.35 var(--font-ui); overflow-wrap: anywhere; }
.challenge-banner-status { overflow: hidden; white-space: nowrap; text-overflow: ellipsis; font: 400 var(--t-small)/1.4 var(--font-ui); color: var(--text-2); }
.daily-challenge-banner > :deep(.ic) { flex-shrink: 0; color: var(--text-2); }
</style>
